import { useState, useMemo, useEffect, useRef } from "react";
import useMediaQuery from "@mui/material/useMediaQuery";
import { useTheme } from "@mui/material/styles";
import { makeStyles } from "tss-react/mui";
import { Link as RouterLink, useParams, useNavigate } from "react-router";
import {
  Typography,
  Avatar,
  Box,
  Menu,
  MenuItem,
  Button,
  Paper,
  Tooltip,
  Card,
  Grid,
  CardActionArea,
  CardContent,
} from "@mui/material";
import LiveTvIcon from "@mui/icons-material/LiveTv";
import Skeleton from "@mui/material/Skeleton";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import PersonAddIcon from "@mui/icons-material/PersonAdd";
import TimelineIcon from "@mui/icons-material/Timeline";
import OndemandVideoIcon from "@mui/icons-material/OndemandVideo";
import ImageSearchIcon from "@mui/icons-material/ImageSearch";
import Done from "@mui/icons-material/Done";
import Person from "@mui/icons-material/Person";
import BubbleChart from "@mui/icons-material/BubbleChart";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import ResponsiveTabs from "@nualang/nualang-ui-components/dist/Navigation/ResponsiveTabs";
import TopicNotFound from "@nualang/nualang-ui-components/dist/Misc/NotFound/Topic";
import ExerciseList from "@nualang/nualang-ui-components/dist/Lists/Exercises";
import PhraseList from "@nualang/nualang-ui-components/dist/Lists/Phrases";
import UpdateTopic from "@nualang/nualang-ui-components/dist/Forms/UpdateTopic";
import UpgradeSubscription from "@nualang/nualang-ui-components/dist/Dialogs/UpgradeSubscription/UpgradeSubscription";
import ShareDrawer from "@nualang/nualang-ui-components/dist/Misc/ShareButton/ShareDrawer";
import { randomId } from "@nualang/nualang-ui-components/dist/utils/index";
import PlaceholderImages from "@nualang/nualang-ui-components/dist/utils/placeholder-images";
import useConfirm from "@nualang/nualang-ui-components/dist/hooks/useConfirm";
import ProgressiveCardMedia from "@nualang/nualang-ui-components/dist/Misc/ProgressiveCardMedia";
import TopicDifficultyBadge from "@nualang/nualang-ui-components/dist/Misc/TopicDifficultyBadge";
import CreatorNotSubscribed from "@nualang/nualang-ui-components/dist/Dialogs/CreatorNotSubscribed/CreatorNotSubscribed";
import Overlay from "@nualang/nualang-ui-components/dist/Misc/Overlay";
import DefaultButton from "@nualang/nualang-ui-components/dist/Misc/DefaultColourButton";
import { CardAvatar, CardMenuIcon } from "@nualang/nualang-ui-components/dist/Cards/CardElements";
import UploadVideo from "@nualang/nualang-ui-components/dist/Dialogs/UploadVideo";
import VideoViewer from "@nualang/nualang-ui-components/dist/Dialogs/VideoViewer/VideoViewer";
import ResponsiveDialog from "@nualang/nualang-ui-components/dist/Dialogs/ResponsiveDialog";
import PdfUploader from "@nualang/nualang-ui-components/dist/Forms/PdfUploader";
import PDFViewer from "@nualang/nualang-ui-components/dist/Dialogs/PDFViewer/PDFViewer";
import { glowPulseLight, glowPulseDark } from "@nualang/nualang-ui-components/dist/utils/Styles";
import ShareIcon from "@mui/icons-material/Share";
import PictureAsPdf from "@mui/icons-material/PictureAsPdf";
import ExitToApp from "@mui/icons-material/ExitToApp";
import { Add } from "@mui/icons-material";
import Delete from "@mui/icons-material/Delete";
import FileCopy from "@mui/icons-material/FileCopy";
import OndemandVideo from "@mui/icons-material/OndemandVideo";
import logger from "@nualang/nualang-ui-components/dist/logger";
import ColorLinearProgress from "@nualang/nualang-ui-components/dist/Misc/ColorLinearProgress";
import { calcTopicCompletions } from "@nualang/nualang-ui-components/dist/utils/index";
import AutoFixHighIcon from "@mui/icons-material/AutoFixHigh";
import config from "../../../config";
import NualangIcon from "../../../img/logo/icon.png";
import { bots as botQuerys, courses as courseQuerys, roleplays as roleplayQuerys } from "@nualang/nualang-api-and-queries/Queries";
import {
  createBot as createBotAPI, getBots,
} from "@nualang/nualang-api-and-queries/APIs/Bots";
import {
  createRoleplay as createRoleplayAPI,
  getRoleplays,
} from "@nualang/nualang-api-and-queries/APIs/Roleplays";
import {
  bots as botHelpers,
  roleplays as roleplayHelpers,
} from "../../../utils/helpers";
import { v4 as uuidv4 } from "uuid";
import { useApp } from "../../../hooks";
import { difficulties } from "../../../utils/index"
import { useQueryClient, useInfiniteQuery } from "@tanstack/react-query";
import useDebounce from "@nualang/nualang-ui-components/dist/hooks/useDebounce";
import { UploadBotImage } from "../../../utils/imageUpload";

const useStyles = makeStyles()((theme) => ({
  root: {
    height: "100%",
    padding: theme.spacing(1, 2),
    [theme.breakpoints.up("sm")]: {
      padding: theme.spacing(2, 3),
    },
  },
  cardTop: {
    position: "relative",
  },
  cardTopAvatar: {
    [theme.breakpoints.down("md")]: {
      width: "60px",
      height: "60px",
    },
    width: "75px",
    height: "75px",
    border: `3px solid ${theme.palette.background.paper}`,
    backgroundColor: theme.palette.mode === "dark" ? "white" : "",
    color:
      theme.palette.mode === "dark" ? theme.palette.background.default : "",
    cursor: "pointer",
    transition: `transform 0.2s ease 0s`,
    "&:hover": {
      transform: `scale(1.08)`,
    },
  },
  header: {
    display: "flex",
    alignItems: "start",
    marginBottom: theme.spacing(2),
    marginLeft: theme.spacing(2),
  },
  headerText: {
    flexGrow: 1,
  },
  headerTextTitle: {
    fontWeight: 600,
  },
  avatar: {
    height: 40,
    width: 40,
    marginRight: theme.spacing(2),
  },
  navCardText: {
    fontWeight: 500,
  },
  coursesTab: {
    position: "relative",
  },
}));
function OverflowMenu({
  t,
  topicId,
  anchorEl,
  handleClose,
  isCreator,
  isMember,
  joinCourse,
  leaveCourse,
  deleteTopic,
  duplicateTopic,
  subscription,
  topicName,
  trackRecommendedEvent,
  mainSiteUrl,
  deleteTopicVideo,
  videoUrl,
  handleUploadVideo,
  loading,
  pdfUrl,
  deletePDF,
  verificationStatus,
  handleClickOpenUpload,
  isTeacher,
  video,
  setVideo,
}) {
  const [isShareDrawerOpen, setIsShareDrawerOpen] = useState(false);
  const [isUploadVideoOpen, setIsUploadVideoOpen] = useState(false);
  const [isUpgradeSubscriptionOpen, setIsUpgradeSubscriptionOpen] =
    useState(false);

  const handleOpenUpgradeSubscriptionModal = () =>
    setIsUpgradeSubscriptionOpen(true);
  const handleCloseUpgradeSubcriptionModal = () =>
    setIsUpgradeSubscriptionOpen(false);
  const handleSubscriptionPage = () =>
    window.open(`${mainSiteUrl}/pricing/app`, "_blank");

  const handleClick = () => {
    if (!subscription.isSubscriptionLoaded || subscription.isPaidUser) {
      setIsShareDrawerOpen(true);
    } else {
      handleOpenUpgradeSubscriptionModal();
    }
  };

  return (
    <>
      <Menu
        id={`card-menu-${topicId}${randomId()}`}
        anchorEl={anchorEl}
        keepMounted
        open={Boolean(anchorEl)}
        onClose={handleClose}
      >
        {!isCreator && !isMember && (
          <MenuItem onClick={joinCourse}>
            <CardMenuIcon>
              <Add fontSize='small' />
            </CardMenuIcon>
            <Typography>{t("join_course")}</Typography>
          </MenuItem>
        )}
        {!isCreator && isMember && (
          <MenuItem onClick={leaveCourse}>
            <CardMenuIcon>
              <ExitToApp fontSize='small' />
            </CardMenuIcon>
            <Typography>{t("leave_course")}</Typography>
          </MenuItem>
        )}
        {isTeacher && (
          <MenuItem onClick={handleClick} data-cy='share-topic'>
            <CardMenuIcon>
              <ShareIcon fontSize='small' />
            </CardMenuIcon>
            <Typography>{t("share")}</Typography>
          </MenuItem>
        )}
        {isCreator && (
          <MenuItem onClick={deleteTopic}>
            <CardMenuIcon>
              <Delete fontSize='small' />
            </CardMenuIcon>
            <Typography>{t("delete_topic")}</Typography>
          </MenuItem>
        )}
        {isCreator && (
          <MenuItem onClick={duplicateTopic}>
            <CardMenuIcon>
              <FileCopy fontSize='small' />
            </CardMenuIcon>
            <Typography>{t("duplicate_topic")}</Typography>
          </MenuItem>
        )}
        {isCreator && verificationStatus === "approved" && (
          <MenuItem
            onClick={() => setIsUploadVideoOpen(true)}
            data-cy='upload-video'
          >
            <CardMenuIcon>
              <OndemandVideo fontSize='small' />
            </CardMenuIcon>
            <Typography>{t("upload_video")}</Typography>
          </MenuItem>
        )}
        {videoUrl && isCreator && verificationStatus === "approved" && (
          <MenuItem onClick={deleteTopicVideo} data-cy='delete-video'>
            <CardMenuIcon>
              <Delete fontSize='small' />
            </CardMenuIcon>
            <Typography>{t("delete_video")}</Typography>
          </MenuItem>
        )}
        {isCreator && verificationStatus === "approved" && (
          <MenuItem onClick={handleClickOpenUpload} data-cy='upload-pdf'>
            <CardMenuIcon>
              <PictureAsPdf fontSize='small' />
            </CardMenuIcon>
            <Typography>{t("upload_pdf")}</Typography>
          </MenuItem>
        )}
        {pdfUrl && isCreator && verificationStatus === "approved" && (
          <MenuItem onClick={deletePDF} data-cy='delete-pdf'>
            <CardMenuIcon>
              <Delete fontSize='small' />
            </CardMenuIcon>
            <Typography>{t("delete_pdf")}</Typography>
          </MenuItem>
        )}
      </Menu>
      <ShareDrawer
        t={t}
        title={topicName}
        url={window.location.href}
        open={isShareDrawerOpen}
        handleClose={() => setIsShareDrawerOpen(false)}
        subscription={subscription}
        trackRecommendedEvent={trackRecommendedEvent}
        itemId={topicId}
        contentType='topic'
      />
      <UpgradeSubscription
        key={"UpgradeSubscription"}
        t={t}
        open={isUpgradeSubscriptionOpen}
        handleClose={handleCloseUpgradeSubcriptionModal}
        onConfirm={handleSubscriptionPage}
        subscription={subscription}
      />
      <UploadVideo
        t={t}
        open={isUploadVideoOpen}
        onSubmit={handleUploadVideo}
        loading={loading}
        video={video}
        setVideo={setVideo}
        handleClose={() => setIsUploadVideoOpen(false)}
      />
    </>
  );
}

function NavCard({ classes, title, subtitle, onClick, picture, type }) {
  return (
    <Card sx={{ height: "100px" }}>
      <CardActionArea onClick={onClick} sx={{ height: "100px" }}>
        <CardContent>
          {subtitle && (
            <Typography
              variant='body2'
              component='div'
              color='textSecondary'
              className={classes.navCardText}
              gutterBottom
            >
              {subtitle}
            </Typography>
          )}
          <Grid
            container
            spacing={1}
            sx={{
              alignItems: 'center',
              width: "100%"
            }}>
            {title && (
              <Grid size={9}>
                <Typography
                  variant='subtitle1'
                  component='div'
                  className={classes.navCardText}
                  noWrap
                >
                  {title}
                </Typography>
              </Grid>
            )}
            <Grid container size={3} sx={{
              justifyContent: 'flex-end'
            }}>
              {picture && <Avatar alt='' src={picture} />}
              {!picture && !type && (
                <Avatar alt=''>
                  <ImageSearchIcon />
                </Avatar>
              )}
              {!picture && type === "back_to_course" && (
                <BubbleChart
                  sx={(theme) => ({
                    color:
                      theme.palette.mode === "dark"
                        ? "#FFFFFF"
                        : "rgba(0,0,0,0.54)",
                    height: "40px",
                    width: "40px",
                  })}
                />
              )}
              {!picture && type === "completed" && (
                <Done
                  sx={(theme) => ({
                    color: theme.palette.success
                      ? theme.palette.success.main
                      : "",
                    height: "40px",
                    width: "40px",
                  })}
                />
              )}
            </Grid>
          </Grid>
        </CardContent>
      </CardActionArea>
    </Card>
  );
}

function Topic({
  t,
  isLoading,
  courseId,
  sectionId,
  topic = {},
  roleplays = [],
  bots = [],
  avatars,
  handleStartCourse,
  handleLeaveCourse,
  handleDeleteTopic,
  handleDuplicateTopic,
  learnLang,
  forLang,
  isMember,
  isCreator,
  handleTranslate,
  handleSpeak,
  clearQueue,
  stopAudio,
  handlePlayAudio,
  micPlaybackEnabled,
  handleUpdateTopic,
  handleUpdateRoleplays,
  handleUpdateBots,
  handleComplete,
  openSnackbar,
  handleRemoveRoleplay,
  handleRemoveBot,
  handleToggleExerciseVisibility,
  handleToggleExerciseWordBank,
  handleUpdateRoleplay,
  handleRoleplayVisibility,
  handleUpdateBot,
  updateBotVisibility,
  viewNextTopic,
  viewPrevTopic,
  handleMessageBot,
  userAttributes,
  voices = {},
  forLangCharacters,
  learnLangCharacters,
  topicProgress,
  handleDeleteProgress,
  handleSaveProgress,
  nextTopic,
  prevTopic,
  isCourseCreator,
  handleEditBot,
  handlePlayBot,
  handlePlayRoleplay,
  handleShareRoleplay,
  handleShareBot,
  handleEditRoleplay,
  handleDuplicateBot,
  handleDuplicateRoleplay,
  handleDuplicatePhraseList,
  fileSizeLimit,
  handleUploadBotExerciseImage,
  correctSound,
  incorrectSound,
  handleViewUserProfile,
  toggleMuteSound,
  subscription,
  creatorSubscription,
  course,
  handleReportProblem,
  languageInformation,
  handleOpenSelectClassroomDialog,
  teachClassrooms,
  isStudent,
  uploadAudioAttempt,
  mainSiteUrl,
  trackRecommendedEvent,
  authenticated,
  handleSignIn,
  languageTag,
  siteLanguage,
  courseSettings,
  pdfUrl,
  pdfTitle,
  handleDeletePDF,
  handleToggleKeyboardTranslation,
  videoUrl,
  loading,
  handleDeleteTopicVideo,
  verificationStatus,
  parentClassroom,
  isUserInternal,
  isCommunityAccessRestricted,
  isReadWriteModeStudent = false,
  isChallengeModeStudent = false,
  isNualangLiveEnabled,
  createdGame,
  username,
  isClassroomCreator,
  isVideoChatEnabled,
  isVideoChatEnabledInSettings,
  handleCreatePhraseList,
  handleUpdatePhraseList,
  handleDeletePhraseList,
  handlePlayPhraseList: handlePlayPhraseListProp,
  handleEditPhraseList: handleEditPhraseListProp,
  authData,
  focusExerciseId,
  ...otherProps
}) {
  const { classes } = useStyles();
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down("md"));
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const [isViewVideoOpen, setIsViewVideoOpen] = useState(false);
  const [file, setFile] = useState(null);
  const [isUploadPdfOpen, setIsUploadPdfOpen] = useState(false);
  const [pageNumber, setPageNumber] = useState(1);
  const [scale, setScale] = useState(1);
  const [animatePlayVideo, setAnimatePlayVideo] = useState(true);
  const [video, setVideo] = useState();
  const [chatbotTemplates, setChatbotTemplates] = useState([]);
  const [scenarioTemplates, setScenarioTemplates] = useState([]);
  const [search, setSearch] = useState("");
  const debouncedSearch = useDebounce(search, 500);
  const [filters, setFilter] = useState({
    difficulty: "all",
    learnLang: "",
    forLang: "",
    orderBy: "",
    limit: 12,
    visibility: "public",
    returnvalues: "",
    lastEvaluatedKey: "",
    lastEvaluatedBotId: "",
    lastEvaluatedCreatedAt: "",
    lastEvaluatedExerciseStartTotal: "",
    sort: "popular",
    search: "",
    verbose: false,
    page: 1,
  })

  const {
    topicId,
    topicName,
    description,
    hiddenExercises = [],
    picture,
    pictureXs,
    phrases = [],
    completions = [],
    hiddenWordBankExercises = [],
    createdBy,
    userImage,
    createdByName,
    banner,
    gameAward,
    isKeyboardHiddenTranslation,
    bannerXs,
  } = topic;

  const {startLoading, stopLoading, handleOpenModerateDialog} = useApp();

  const queryClient = useQueryClient();

  const handleOpenPhraseList = (phraseList) => handlePlayPhraseListProp?.(phraseList);
  const handleEditPhraseList = (phraseList) => handleEditPhraseListProp?.(phraseList);

  const navigate = useNavigate();
  const params = useParams();
  const { classroomId } = params;

  const [confirm] = useConfirm(t);

  useEffect(() => {
      if (filters.learnLang !== learnLang) {
        setFilter((prevFilters) => ({
          ...prevFilters,
          learnLang: learnLang,
        }));
      }
      if (filters.search !== debouncedSearch) {
        setFilter((prevFilters) => ({
          ...prevFilters,
          search: debouncedSearch,
        }));
      }
    }, [username, filters.learnLang, filters.search, learnLang, debouncedSearch]);

  useEffect(() => {
    let initBotTemplates = [];
    let initScenarioTemplates = [];

    if (learnLang) {
      const langConfig = config.languages[`${learnLang}`];
          initBotTemplates = (langConfig?.botBlueprints || []).map(
            (b) => ({
              ...b,
              userImage: NualangIcon,
              createdByName: "Nualang",
            }),
          );
           initScenarioTemplates = (langConfig?.roleplayBlueprints || []).map((b) => ({
                  ...b,
                  userImage: NualangIcon,
                  createdByName: "Nualang",
                }));
        }
    setChatbotTemplates(initBotTemplates);
    setScenarioTemplates(initScenarioTemplates);
  }, [learnLang]);
  
  const {
    data: botsData = {},
    fetchNextPage: fetchNextBotsPage,
    isFetchingNextPage: isFetchingNextBotsPage,
    hasNextPage: hasNextBotsPage,
  } = useInfiniteQuery({
    queryKey: [...botQuerys.botKeys.list(filters), "create"],
    queryFn: async ({ pageParam }) => {
      try {
        const bots = await getBots({
          ...filters,
          page: pageParam.page || 1,
          lastEvaluatedBotId: pageParam.botId ? pageParam.botId : "",
          lastEvaluatedCreatedAt: pageParam.createdAt
            ? pageParam.createdAt
            : "",
          lastEvaluatedKey: pageParam.botId ? pageParam.botId : "",
          lastEvaluatedExerciseStartTotal:
            pageParam.exerciseStartTotal || pageParam.exerciseStartTotal === 0
              ? pageParam.exerciseStartTotal
              : "",
        });
        const formattedBots = await botHelpers.formatBots(
          bots.Items,
          authData,
        );
        return {
          ...bots,
          Items: formattedBots,
        };
      } catch (error) {
        openSnackbar("search_error", "error");
        throw error;
      }
    },
    initialPageParam: {},
    getNextPageParam: (lastPage) => {
      if (
        lastPage.Items.length > 0 &&
        (lastPage.LastEvaluatedKey?.page || lastPage.LastEvaluatedKey?.botId)
      ) {
        return lastPage.LastEvaluatedKey;
      }
      return undefined;
    },
    enabled: !isCommunityAccessRestricted,
  });

  const teachBotsQuery = botQuerys.useBots(
      async (filters) => {
        const bots = await getBots(filters);
        const formattedBots = await botHelpers.formatBots(bots.Items, username);
        return formattedBots;
      },
      {
        filters: {
          createdBy: username,
          // Get all using high limit
          limit: config.limits.bots,
          learnLang: learnLang,
        },
      },
      { enabled: !!username },
    );
  
    const teachBots =
      teachBotsQuery.isSuccess &&
      Array.isArray(teachBotsQuery.data) &&
      teachBotsQuery.data.length
        ? teachBotsQuery.data.filter(
            (bot) =>
              bot.courseSectionTopicId !== `${courseId}|${sectionId}|${topicId}`,
          )
        : [];

    const allBots = useMemo(
      () => (botsData.pages || []).flatMap((page) => page.Items),
      [botsData],
    );

  const {
      data: roleplaysData = {},
      isLoading: isLoadingCommunityRoleplays,
      isFetchingNextPage: isFetchingNextCommunityRoleplaysPage,
      fetchNextPage: fetchNextCommunityRoleplaysPage,
      hasNextPage: hasNextCommunityRoleplaysPage,
    } = useInfiniteQuery({
      queryKey: [...roleplayQuerys.roleplayKeys.list(filters), "create"],
      queryFn: async ({ pageParam }) => {
        try {
          const roleplays = await getRoleplays({
            ...filters,
            page: pageParam.page || 1,
            lastEvaluatedRoleplayId: pageParam.roleplayId
              ? pageParam.roleplayId
              : "",
            lastEvaluatedCreatedAt: pageParam.createdAt
              ? pageParam.createdAt
              : "",
            lastEvaluatedKey: pageParam.roleplayId ? pageParam.roleplayId : "",
            lastEvaluatedExerciseStartTotal:
              pageParam.exerciseStartTotal || pageParam.exerciseStartTotal === 0
                ? pageParam.exerciseStartTotal
                : "",
          });
          const formattedRoleplays = await roleplayHelpers.formatRoleplays(
            roleplays.Items,
            authData,
          );
          return {
            ...roleplays,
            Items: formattedRoleplays,
          };
        } catch (error) {
          openSnackbar("search_error", "error");
          throw error;
        }
      },
      initialPageParam: {},
      getNextPageParam: (lastPage) => {
        if (
          lastPage.Items.length > 0 &&
          (lastPage.LastEvaluatedKey?.page ||
            lastPage.LastEvaluatedKey?.roleplayId)
        ) {
          return lastPage.LastEvaluatedKey;
        }
        return undefined;
      },
      enabled: !isCommunityAccessRestricted,
    });
  
    const allRoleplays = useMemo(
      () => (roleplaysData.pages || []).flatMap((page) => page.Items),
      [roleplaysData],
    );

  const handleUploadVideo = async (video) => {
    setIsSubmitting(true);
    try {
      await handleUpdateTopic(
        {
          video,
        },
        isSubmitting,
      );
      setIsSubmitting(false);
    } catch (error) {
      setIsSubmitting(false);
      console.error(error);
    }
  };

  const handleOpenViewVideo = () => {
    setIsViewVideoOpen(true);
    setAnimatePlayVideo(false);
  };

  const deleteTopicVideo = async () => {
    const confirmed = await confirm(
      t("delete_video"),
      t("delete_video_confirmation"),
    );
    if (confirmed) {
      await handleDeleteTopicVideo(sectionId, topicId, videoUrl);
      setVideo(null);
      handleCloseMenu();
    }
  };

  const [isCreatorNotSubscribedOpen, setIsCreatorNotSubscribedOpen] =
    useState(false);
  const handleOpenCreatorNotSubscribedModal = () =>
    setIsCreatorNotSubscribedOpen(true);
  const handleCloseCreatorNotSubscribedModal = () =>
    setIsCreatorNotSubscribedOpen(false);

  const handleClickOpenMenu = (event) => setAnchorEl(event.currentTarget);
  const handleCloseMenu = () => setAnchorEl(null);

  const handleUploadSubmit = async () => {
    setIsSubmitting(true);
    try {
      await handleUpdateTopic({ pdf: file }, isSubmitting);
    } catch (error) {
      logger.error(error);
    }
    setIsUploadPdfOpen(false);
    setIsSubmitting(false);
    setFile(null);
    handleCloseMenu();
  };

  const createBotMutation = botQuerys.useCreateBot(createBotAPI, {
    onSettled: () => {
      // Error or success... doesn't matter!
      stopLoading();
    },
    onSuccess: async (data) => {
      const newBot = data.Item;
      await botHelpers.invalidateBotCache(newBot);
      openSnackbar(`${t("bot_created")}`, "success");
      const { botId } = newBot;
      queryClient.invalidateQueries({
        queryKey: botQuerys.botKeys.lists(),
      });
      if (courseId) {
        queryClient.invalidateQueries({
          queryKey: courseQuerys.courseKeys.itemSections(courseId, username),
        });
      }
      if (classroomId && courseId && sectionId && topicId) {
        navigate(
          `/classrooms/${classroomId}/${courseId}/${sectionId}/${topicId}/bots/${botId}/edit`,
        );
      } else if (courseId && sectionId && topicId) {
        navigate(
          `/courses/${courseId}/${sectionId}/${topicId}/bots/${botId}/edit`,
        );
      } else {
        navigate(`/bots/${botId}/edit`);
      }
    },
    onError: () => {
      openSnackbar("problem_creating", "error");
    },
  });

  const uploadAndModerateBotImage = async (values, { field, uploadId }) => {
    const warnDetailsField = `${field}WarnDetails`;
    const s3KeyField = `${field}S3Key`;
    values[warnDetailsField] = {};
    const base64String = values[field].split(",");
    const [uploadedImage, uploadedS3Key, moderated, warn, moderatedLabels] =
      await UploadBotImage(base64String[1] || base64String[0], uploadId);
    if (moderated || warn) {
      if (verificationStatus === "approved" || warn) {
        const confirmation = await confirm(
          `${t("warning")}`,
          `${t("moderation_warning")}${moderatedLabels.join(", ")}`,
          `${t("use_anyways")}`,
        );
        values[warnDetailsField] = {
          user: username,
          labels: moderatedLabels.join(", "),
        };
        if (confirmation !== true) {
          return true;
        }
      } else {
        handleOpenModerateDialog(moderatedLabels[0]);
        return true;
      }
    }
    if (uploadedImage) {
      values[field] = uploadedImage;
      values[s3KeyField] = uploadedS3Key;
    } else {
      delete values[field];
    }
    return false;
  };

  const createBot = async (values) => {
    try {
      const { visibility, difficulty, tags } = values;
      if (
        (visibility !== "public" && visibility !== "private") ||
        !difficulties.includes(difficulty) ||
        !Array.isArray(tags)
      ) {
        //input validation
        throw new Error();
      }
      const imageId = uuidv4();
      if (values.picture) {
        const aborted = await uploadAndModerateBotImage(values, {
          field: "picture",
          uploadId: imageId,
        });
        if (aborted) return;
      }
      if (values.botAvatar) {
        const aborted = await uploadAndModerateBotImage(values, {
          field: "botAvatar",
          uploadId: `${imageId}-botAvatar`,
        });
        if (aborted) return;
      }
      delete values.botId;
      startLoading();

    const botToCreate = {...values, idx: Date.now()}

    return createBotMutation.mutateAsync({values: botToCreate});
    } catch (error) {
      openSnackbar("Error creating bot", "error");
      logger.error(error);
    }
  };

  const createRoleplayMutation = roleplayQuerys.useCreateRoleplay(
      createRoleplayAPI,
      {
        onSettled: () => {
          // Error or success... doesn't matter!
          stopLoading();
        },
        onSuccess: async (data) => {
          const newRoleplay = data.Item;
          await roleplayHelpers.invalidateRoleplayCache(newRoleplay);
          openSnackbar("Scenario Created", "success");
          queryClient.invalidateQueries({
            queryKey: roleplayQuerys.roleplayKeys.list({
              createdBy: username,
              limit: config.limits.roleplays,
            }),
          });
          queryClient.invalidateQueries({
            queryKey: roleplayQuerys.roleplayKeys.list({
              courseSectionTopicId: `${courseId}|${sectionId}|${topicId}`,
              limit: config.limits.roleplays,
            }),
          });
          if (courseId) {
            queryClient.invalidateQueries({
              queryKey: courseQuerys.courseKeys.itemSections(courseId, username),
            });
          }
          const { roleplayId } = newRoleplay;
          if (classroomId && courseId && sectionId && topicId) {
            navigate(
              `/classrooms/${classroomId}/${courseId}/${sectionId}/${topicId}/roleplays/${roleplayId}/edit`,
            );
          } else if (courseId && sectionId && topicId) {
            navigate(
              `/courses/${courseId}/${sectionId}/${topicId}/roleplays/${roleplayId}/edit`,
            );
          } else {
            navigate(`/roleplays/${roleplayId}/edit`);
          }
        },
        onError: () => {
          openSnackbar("problem_creating", "error");
        },
      },
    );
  
    const createRoleplay = (values, setSubmitting) => {
      const { roleplayName, script } = values;
  
      if (!roleplayName || !script || !Array.isArray(script)) {
        openSnackbar("missing_details", "error");
        return;
      }
  
      startLoading();
      const roleplayToCreate = { ...values, idx: Date.now() };
      return createRoleplayMutation.mutateAsync({ values: roleplayToCreate });

    };

  // Same create call as above but WITHOUT the navigate() in onSuccess, so we can
  // create several scenarios in a row and stay on the topic screen.
  const addRoleplayToTopicMutation = roleplayQuerys.useCreateRoleplay(
    createRoleplayAPI,
    {
      onSuccess: async (data) => {
        await roleplayHelpers.invalidateRoleplayCache(data.Item);
      },
      onError: () => {
        openSnackbar("problem_creating", "error");
      },
    },
  );

  // Build the create payload for a single selected template/community scenario,
  // tied to this topic. Mirrors handleSelectRoleplayTemplate in CreateRoleplay,
  // but produces a fresh object (never mutates the selection) and sets the
  // courseSectionTopicId itself since there is no form to do it.
  const buildRoleplayValuesFromTemplate = (roleplay) => {
    // shallow copy so we never mutate the selection held by the dialog
    const values = { ...roleplay };
    // regenerated / owned by the new scenario, so drop the template's versions
    delete values.roleplayId;
    delete values.courseSectionTopicId;
    delete values.exerciseCompletionTotal;
    delete values.exerciseStartTotal;
    // owner metadata is set below from the current user, never the template's
    delete values.createdBy;

    let selectedDifficulty = "A1";
    if (topic?.difficulty && topic.difficulty.toLowerCase() !== "all") {
      selectedDifficulty = topic.difficulty;
    } else if (
      roleplay.difficulty &&
      roleplay.difficulty.toLowerCase() !== "all"
    ) {
      selectedDifficulty = roleplay.difficulty;
    }

    return {
      ...values,
      selectedTemplate: roleplay.roleplayId,
      visibility: "private",
      createdByName: userAttributes ? userAttributes.preferred_username : null,
      userImage: userAttributes ? userAttributes.picture : null,
      difficulty: selectedDifficulty,
      forLang: course ? course.forLang : forLang || null,
      // Stored roleplays persist an empty description as null; the create
      // endpoint accepts "" but rejects null.
      description: values.description || "",
      courseSectionTopicId:
        courseId && sectionId && topicId
          ? `${courseId}|${sectionId}|${topicId}`
          : undefined,
      idx: Date.now(),
    };
  };

  // Plural version of handleSelectRoleplayTemplate: create every selected
  // scenario against this topic. Used by the "Find Scenario" multi-select flow.
  const handleSelectRoleplayTemplates = async (selectedScenarios) => {
    if (!Array.isArray(selectedScenarios) || selectedScenarios.length === 0) {
      return;
    }

    startLoading();
    try {
      // allSettled so one failed create doesn't abort the batch and skip the
      // cache refresh for scenarios that were created successfully.
      const results = await Promise.allSettled(
        selectedScenarios.map((roleplay) => {
          const values = buildRoleplayValuesFromTemplate(roleplay);
          if (!values.roleplayName || !Array.isArray(values.script)) {
            return Promise.resolve(null);
          }
          return addRoleplayToTopicMutation.mutateAsync({ values });
        }),
      );

      const createdCount = results.filter(
        (result) => result.status === "fulfilled" && result.value,
      ).length;
      const failedCount = results.filter(
        (result) => result.status === "rejected",
      ).length;
      if (createdCount === 0) {
        openSnackbar(
          failedCount > 0 ? "problem_creating" : "missing_details",
          "error",
        );
        return;
      }

      queryClient.invalidateQueries({
        queryKey: roleplayQuerys.roleplayKeys.list({
          createdBy: username,
          limit: config.limits.roleplays,
        }),
      });
      queryClient.invalidateQueries({
        queryKey: roleplayQuerys.roleplayKeys.list({
          courseSectionTopicId: `${courseId}|${sectionId}|${topicId}`,
          limit: config.limits.roleplays,
        }),
      });
      if (courseId) {
        queryClient.invalidateQueries({
          queryKey: courseQuerys.courseKeys.itemSections(courseId, username),
        });
      }
      openSnackbar(t("scenarios_added"), "success");
    } catch (error) {
      logger.error(error);
      openSnackbar("problem_creating", "error");
    } finally {
      stopLoading();
    }
  };

  const teachRoleplaysQuery = roleplayQuerys.useRoleplays(
      async (filters) => {
        const roleplays = await getRoleplays(filters);
        const formattedRoleplays = await roleplayHelpers.formatRoleplays(
          roleplays.Items,
          username,
        );
        return formattedRoleplays;
      },
      {
        filters: {
          createdBy: username,
          limit: config.limits.roleplays,
          learnLang: learnLang,
        },
      },
      {
        enabled: !!username,
      },
    );
  
    const teachRoleplays =
      teachRoleplaysQuery.isSuccess &&
      Array.isArray(teachRoleplaysQuery.data) &&
      teachRoleplaysQuery.data.length
        ? teachRoleplaysQuery.data.filter(
            (roleplay) =>
              roleplay.courseSectionTopicId !==
              `${courseId}|${sectionId}|${topicId}`,
          )
        : [];

  // Same create call as createBot above but WITHOUT the navigate() in onSuccess,
  // so we can create several chatbots in a row and stay on the topic screen.
  const addBotToTopicMutation = botQuerys.useCreateBot(createBotAPI, {
    onSuccess: async (data) => {
      await botHelpers.invalidateBotCache(data.Item);
      queryClient.invalidateQueries({
        queryKey: botQuerys.botKeys.list({
          createdBy: username,
          learnLang: learnLang,
          limit: config.limits.bots,
        }),
      });
    },
    onError: () => {
      openSnackbar("problem_creating", "error");
    },
  });

  // Build the create payload for a single selected template/community chatbot,
  // tied to this topic. Mirrors handleSelectBotTemplate in CreateBot, but
  // produces a fresh object (never mutates the selection) and sets the
  // courseSectionTopicId itself since there is no form to do it.
  const buildBotValuesFromTemplate = (bot, courseSectionTopicId) => {
    // shallow copy so we never mutate the selection held by the dialog
    const values = { ...bot };
    // regenerated / owned by the new chatbot, so drop the template's versions
    delete values.botId;
    delete values.courseSectionTopicId;
    delete values.exerciseCompletionTotal;
    delete values.exerciseStartTotal;
    // owner metadata is set below from the current user, never the template's
    delete values.createdBy;

    let selectedDifficulty = "A1";
    if (topic?.difficulty && topic.difficulty.toLowerCase() !== "all") {
      selectedDifficulty = topic.difficulty;
    } else if (bot.difficulty && bot.difficulty.toLowerCase() !== "all") {
      selectedDifficulty = bot.difficulty;
    }

    return {
      ...values,
      selectedTemplate: bot.botId,
      visibility: "private",
      createdByName: userAttributes ? userAttributes.preferred_username : null,
      userImage: userAttributes ? userAttributes.picture : null,
      difficulty: selectedDifficulty,
      forLang: course ? course.forLang : forLang || null,
      // createBot validation requires tags to be an array
      tags: Array.isArray(bot.tags) ? bot.tags : [],
      courseSectionTopicId,
      idx: Date.now(),
    };
  };

  // Plural version of handleSelectBotTemplate: create every selected chatbot
  // against this topic. Used by the "Find Chatbot" multi-select flow.
  const handleAddChatbotToTopic = async (
    chatbots,
  ) => {
    if (!Array.isArray(chatbots) || chatbots.length === 0) {
      return;
    }

    const courseSectionTopicId =
      courseId && sectionId && topicId
        ? `${courseId}|${sectionId}|${topicId}`
        : undefined;

    startLoading();
    try {
      const results = await Promise.all(
        chatbots.map((bot) => {
          const values = buildBotValuesFromTemplate(bot, courseSectionTopicId);
          if (
            (values.visibility !== "public" &&
              values.visibility !== "private") ||
            !difficulties.includes(values.difficulty) ||
            !Array.isArray(values.tags)
          ) {
            return Promise.resolve(null);
          }
          return addBotToTopicMutation.mutateAsync({ values });
        }),
      );

      const createdCount = results.filter(Boolean).length;
      if (createdCount === 0) {
        openSnackbar("missing_details", "error");
        return;
      }

      queryClient.invalidateQueries({
        queryKey: botQuerys.botKeys.list({
          courseSectionTopicId: `${courseId}|${sectionId}|${topicId}`,
          limit: config.limits.bots,
        }),
      });
      // Also refresh the creator-scoped list read by teachBotsQuery so the
      // newly created bot leaves the picker without a manual refresh.
      queryClient.invalidateQueries({
        queryKey: botQuerys.botKeys.list({
          createdBy: username,
          limit: config.limits.bots,
          learnLang: learnLang,
        }),
      });
      if (courseId) {
        queryClient.invalidateQueries({
          queryKey: courseQuerys.courseKeys.itemSections(courseId, username),
        });
      }
      openSnackbar(t("chatbots_added"), "success");
    } catch (error) {
      logger.error(error);
      openSnackbar("problem_creating", "error");
    } finally {
      stopLoading();
    }
  };

  const handleClickOpenUpload = () => setIsUploadPdfOpen(true);

  const [isViewPdfOpen, setIsViewPdfOpen] = useState(false);
  const handleOpenViewPDF = () => setIsViewPdfOpen(true);
  const handleCloseViewPdf = () => setIsViewPdfOpen(false);

  const topicCompletion = useMemo(() => {
    if (isMember && topic?.topicId && topic?.completions?.length) {
      return calcTopicCompletions(
        topic,
        topic.completions,
        false,
        null,
        isChallengeModeStudent,
        isReadWriteModeStudent,
      ).completion;
    }
    return { completed: 0, total: 0, percentage: 0 };
  }, [isMember, topic?.topicId, topic?.completions?.length, topic?.roleplays?.length, topic?.bots?.length, isChallengeModeStudent, isReadWriteModeStudent]);

  const { percentage, completed, total } = topicCompletion;

  const loadingTabs = [
    { label: t("exercises"), id: "Exercises" },
  ];

  let initialTabValue = 0;
  if (window.location.hash) {
    const selectedTab = decodeURIComponent(window.location.hash);
    const found = loadingTabs.findIndex(
      (tab) => tab.id === selectedTab.replace("#", "")
    );
    if (found >= 0) initialTabValue = found;
  }

  if (topic && Object.keys(topic).length === 0) {
    return (
      <>
        <div className={classes.cardTop}>
          <Skeleton
            variant='rectangular'
            sx={(theme) => ({
              [theme.breakpoints.up("md")]: { paddingTop: "15%" },
              height: 0,
              paddingTop: "25%",
              borderRadius: theme.spacing(2),
              marginBottom: theme.spacing(4),
            })}
          />
        </div>
        <div className={classes.header}>
          <div className={classes.headerText}>
            <Typography variant='h5' component='h1' gutterBottom>
              <Skeleton variant='rectangular' height={32} width={150} />
            </Typography>
            <Typography variant='body2' color='textSecondary'>
              <Skeleton variant='rectangular' height={20} width={250} />
            </Typography>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                mt: 2,
                mb: 1
              }}>
              <Box sx={{
                mr: 1
              }}>
                <Button
                  variant='contained'
                  color='primary'
                  startIcon={<PersonAddIcon />}
                  size='small'
                  disabled
                >
                  {t("join")}
                </Button>
              </Box>
              {authenticated && (
                <DefaultButton
                  startIcon={<MoreVertIcon />}
                  size='small'
                  disabled
                >
                  {t("more_options")}
                </DefaultButton>
              )}
            </Box>
          </div>
        </div>
        <ResponsiveTabs t={t} tabs={loadingTabs} centered initialValue={initialTabValue}>
          <Box sx={{
            py: 1
          }}>
            <ExerciseList
              t={t}
              userAttributes={userAttributes}
              isLoading={isLoading}
              teachClassrooms={teachClassrooms}
              courseSettings={courseSettings}
              trackRecommendedEvent={trackRecommendedEvent}
              isReadWriteModeStudent={isReadWriteModeStudent}
              isChallengeModeStudent={isChallengeModeStudent}
              focusExerciseId={focusExerciseId}
            />
          </Box>
          <Box sx={{
            py: 1
          }}>
            <PhraseList t={t} verificationStatus={verificationStatus} openSnackbar={openSnackbar} />
          </Box>
        </ResponsiveTabs>
      </>
    );
  }

  const updateRoleplays = async (roleplays) => {
    try {
      handleUpdateRoleplays(courseId, sectionId, topicId, roleplays);
    } catch (error) {
      console.error(error);
    }
  };

  const updateBots = async (bots) => {
    try {
      handleUpdateBots(courseId, sectionId, topicId, bots);
    } catch (error) {
      console.error(error);
    }
  };

  const tabs = [
    { label: t("exercises"), id: "Exercises" },
  ];

  if (isCreator) {
    tabs.push({ label: t("settings"), id: "Settings" });
  }

  const handleJoin = () => {
    if (creatorSubscription.isPaidUser || course.visibility === "public") {
      handleStartCourse();
    } else {
      handleOpenCreatorNotSubscribedModal();
    }
  };

  const removeRoleplay = async (roleplay) => {
    const result = await confirm(
      t("delete_roleplay"),
      t("delete_roleplay_confirmation"),
      "",
      true,
      "",
      t("confirmDeletionText"),
      t("save_library"),
      true,
    );
    if (result.confirmed) {
      handleRemoveRoleplay(roleplay, result.saveToLibrary ? true : false);
    }
  };

  const removeBot = async (bot) => {
    const result = await confirm(
      t("delete_bot"),
      t("delete_bot_confirmation"),
      "",
      true,
      "",
      t("confirmDeletionText"),
      t("save_library"),
      true,
    );
    if (result.confirmed) {
      handleRemoveBot(bot, result.saveToLibrary ? true : false);
    }
  };

  const duplicateTopic = async () => handleDuplicateTopic(topic);

  const createDiscussion = () => {
    if (isVideoChatEnabledInSettings) {
      navigate(
        `/classrooms/${classroomId}?courseSectionTopicId=${courseId}|${sectionId}|${topicId}#Discuss`,
      );
    } else {
      navigate(`/classrooms/${classroomId}#Discuss`);
    }
  };

  const deleteTopic = async (topic) => {
    const confirmed = await confirm(
      t("delete_topic"),
      t("delete_topic_confirmation"),
      "",
      true,
      t("topic_deletion_note", {
        roleplayCount: roleplays ? roleplays.length : 0,
        botCount: bots ? bots.length : 0,
        phraseCount: phrases ? phrases.length : 0,
      }),
      t("confirmDeletionText"),
    );
    if (confirmed) handleDeleteTopic(topic);
  };

  const deletePDF = async () => {
    const confirmed = await confirm(
      t("delete_pdf"),
      t("delete_pdf_confirmation"),
    );

    if (confirmed) {
      handleDeletePDF();
      setPageNumber(1);
      setScale(1);
      handleCloseMenu();
    }
  };

  const exercisesBox = (
    <Box className={classes.coursesTab} sx={{
      py: 1
    }}>
      <Paper>
        <ExerciseList
          t={t}
          isLoading={isLoading}
          handleDuplicateBot={handleDuplicateBot}
          handleDuplicateRoleplay={handleDuplicateRoleplay}
          handleDuplicatePhraseList={handleDuplicatePhraseList}
          isCreator={isCreator}
          isMember={isMember}
          isChallengeModeStudent={isChallengeModeStudent}
          phrases={phrases}
          completions={completions}
          roleplays={roleplays}
          bots={bots}
          avatars={avatars}
          hiddenExercises={hiddenExercises}
          hiddenWordBankExercises={hiddenWordBankExercises}
          learnLang={learnLang}
          forLang={forLang}
          handleSpeak={handleSpeak}
          clearQueue={clearQueue}
          stopAudio={stopAudio}
          handlePlayAudio={handlePlayAudio}
          handleTranslate={handleTranslate}
          micPlaybackEnabled={micPlaybackEnabled}
          handleComplete={handleComplete}
          openSnackbar={openSnackbar}
          courseId={courseId}
          sectionId={sectionId}
          topicId={topicId}
          handleRemoveRoleplay={removeRoleplay}
          handleRemoveBot={removeBot}
          updateBots={updateBots}
          updateRoleplays={updateRoleplays}
          handleUpdateRoleplay={handleUpdateRoleplay}
          handleToggleExerciseWordBank={handleToggleExerciseWordBank}
          handleToggleExerciseVisibility={handleToggleExerciseVisibility}
          handleRoleplayVisibility={handleRoleplayVisibility}
          handleUpdateBot={handleUpdateBot}
          updateBotVisibility={updateBotVisibility}
          handleMessageBot={handleMessageBot}
          userAttributes={userAttributes}
          voices={voices}
          disablePadding
          forLangCharacters={forLangCharacters}
          learnLangCharacters={learnLangCharacters}
          topicProgress={topicProgress}
          handleDeleteProgress={handleDeleteProgress}
          handleSaveProgress={handleSaveProgress}
          isCourseCreator={isCourseCreator}
          topicInfo={topic}
          handleEditBot={handleEditBot}
          handlePlayBot={handlePlayBot}
          handlePlayRoleplay={handlePlayRoleplay}
          handleShareRoleplay={handleShareRoleplay}
          handleShareBot={handleShareBot}
          handleEditRoleplay={handleEditRoleplay}
          correctSound={correctSound}
          incorrectSound={incorrectSound}
          handleUploadBotExerciseImage={handleUploadBotExerciseImage}
          toggleMuteSound={toggleMuteSound}
          handleReportProblem={handleReportProblem}
          languageInformation={languageInformation}
          uploadAudioAttempt={uploadAudioAttempt}
          teachClassrooms={teachClassrooms}
          languageTag={languageTag}
          siteLanguage={siteLanguage}
          courseSettings={courseSettings}
          handleToggleKeyboardTranslation={handleToggleKeyboardTranslation}
          isKeyboardHiddenTranslation={isKeyboardHiddenTranslation}
          trackRecommendedEvent={trackRecommendedEvent}
          parentClassroom={parentClassroom}
          isReadWriteModeStudent={isReadWriteModeStudent}
          focusExerciseId={focusExerciseId}
          handleOpenPhraseList={handleOpenPhraseList}
          handleEditPhraseList={handleEditPhraseList}
          handleCreatePhraseList={handleCreatePhraseList}
          handleUpdatePhraseList={handleUpdatePhraseList}
          handleDeletePhraseList={handleDeletePhraseList}
          chatbotTemplates={chatbotTemplates}
          createBot={createBot}
          createRoleplay={createRoleplay}
          handleAddScenarioToTopic={handleSelectRoleplayTemplates}
          handleAddChatbotToTopic={handleAddChatbotToTopic}
          scenarioTemplates={scenarioTemplates}
          langChars={learnLangCharacters}
          difficulty={topic?.difficulty}
          communityChatbots={allBots}
          onLoadMoreCommunityChatbots={fetchNextBotsPage}
          hasMoreCommunityChatbots={hasNextBotsPage}
          isLoadingMoreCommunityChatbots={isFetchingNextBotsPage}
          libraryChatbots={teachBots}
          communityRoleplays={allRoleplays}
          onLoadMoreCommunityRoleplays={fetchNextCommunityRoleplaysPage}
          hasMoreCommunityRoleplays={hasNextCommunityRoleplaysPage}
          isLoadingMoreCommunityRoleplays={isFetchingNextCommunityRoleplaysPage}
          libraryRoleplays={teachRoleplays}
          isLoadingCommunityRoleplays={isLoadingCommunityRoleplays}
          topicPicture={topic?.picture}
          isUserInternal={isUserInternal}
          isCommunityAccessRestricted={isCommunityAccessRestricted}
        />
      </Paper>
      {!(isMember || isCreator) && (
        <Overlay
          description={
            !authenticated
              ? t("course_sign_in_to_access")
              : t("join_course_to_access")
          }
          buttonText={!authenticated ? t("sign_in") : t("join")}
          onClick={!authenticated ? handleSignIn : handleJoin}
        />
      )}
    </Box>
  );

  return (
    <>
      <div className={classes.cardTop}>
        <ProgressiveCardMedia
          sx={(theme) => ({
            [theme.breakpoints.up("md")]: { paddingTop: "15%" },
            height: 0,
            paddingTop: "25%",
            borderRadius: theme.spacing(2),
            marginBottom: theme.spacing(4),
          })}
          highQualitySrc={(banner ?? picture) || PlaceholderImages.topic}
          lowQualitySrc={bannerXs ?? pictureXs}
          title={topicName}
        />
        <TopicDifficultyBadge difficulty={topic?.difficulty} t={t} />
        {userImage ? (
          <CardAvatar
            aria-label={`${t("go_to")} ${createdByName || `${topicName} ${t("creator")}`} ${t("profile")}`}
            src={userImage}
            className={classes.cardTopAvatar}
            onClick={() => handleViewUserProfile(createdBy)}
            alt=''
            onKeyDown={(event) => {
              if (event.key === "Enter") {
                handleViewUserProfile(createdBy);
              }
            }}
            role='link'
            tabIndex={0}
            verificationStatus={verificationStatus}
            gameAward={gameAward}
            title={createdByName || `${topicName} creator`}
            sx={(theme) => ({
              backgroundColor: theme.palette.mode === "dark" ? "#ffffff" : "#bdbdbd",
              color: theme.palette.mode === "dark" ? "#ffffff" : "#bdbdbd",
            })}
          />
        ) : (
          <CardAvatar
            aria-label={`${t("go_to")} ${createdByName || `${topicName} ${t("creator")}`} ${t("profile")}`}
            sx={(theme) => ({
              backgroundColor: theme.palette.mode === "dark" ? "#ffffff" : "#bdbdbd",
              color: theme.palette.mode === "dark" ? theme.palette.background.default : "white",
            })}
            alt=''
            className={classes.cardTopAvatar}
            onClick={() => handleViewUserProfile(createdBy)}
            onKeyDown={(event) => {
              if (event.key === "Enter") {
                handleViewUserProfile(createdBy);
              }
            }}
            role='link'
            tabIndex={0}
            verificationStatus={verificationStatus}
            gameAward={gameAward}
            title={createdByName || `${topicName} creator`}
          >
            <Person fontSize='large' />
          </CardAvatar>
        )}
      </div>
      <div className={classes.header}>
        <div className={classes.headerText}>
          <Typography
            variant='h5'
            component='h1'
            className={classes.headerTextTitle}
          >
            <span data-cy='topic-title'>{topicName}</span>
          </Typography>
          <Typography variant='body2' color='textSecondary' gutterBottom>
            {description}
          </Typography>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              mt: 2,
              mb: 1,
              [theme.breakpoints.down("md")]: { flexWrap: "wrap", gap: 1 }
            }}>
            {isNualangLiveEnabled && classroomId && (
              <Box sx={{
                mr: 1
              }}>
                <Tooltip title={t("play_live_desc")}>
                  <span>
                    <Button
                      variant='contained'
                      color='primary'
                      startIcon={<LiveTvIcon />}
                      size='small'
                      id='play-live'
                      sx={{
                        animation:
                          createdGame && createdGame.createdBy === username
                            ? `${theme.palette.mode === "light" ? glowPulseLight : glowPulseDark} 2s infinite ease-in-out`
                            : null,
                        boxShadow:
                          createdGame && createdGame.createdBy === username
                            ? `0 0 0 0 ${theme.palette.mode === "light" ? "rgba(101, 0, 221, 0.7)" : "rgba(171, 120, 255, 0.7)"}`
                            : null,
                      }}
                      onClick={() => {
                        if (createdGame) {
                          if (createdGame.createdBy === username) {
                            navigate(
                              `/classrooms/${classroomId}/games/${createdGame.gameId}`,
                              { state: { isGameCreator: true } },
                            );
                          }
                        } else {
                          navigate(
                            `/classrooms/${classroomId}?courseSectionTopicId=${courseId}|${sectionId}|${topicId}#Live`,
                          );
                        }
                      }}
                    >
                      {t(
                        createdGame && createdGame.createdBy === username
                          ? "open_live_game"
                          : "",
                      )}
                      {t(
                        createdGame && createdGame.createdBy !== username
                          ? "join_live_game"
                          : "",
                      )}
                      {t(!createdGame ? "play_live" : "")}
                    </Button>
                  </span>
                </Tooltip>
              </Box>
            )}
            {isClassroomCreator && classroomId && isVideoChatEnabled && (
              <Box sx={{
                mr: 1
              }}>
                <Button
                  variant='contained'
                  color='success'
                  startIcon={<AutoFixHighIcon />}
                  size='small'
                  onClick={createDiscussion}
                >
                  {t("generate_discussion")}
                </Button>
              </Box>
            )}
            {videoUrl && (isMember || isCreator) && (
              <Box sx={{
                mr: 1
              }}>
                <Button
                  variant='contained'
                  color='primary'
                  startIcon={<OndemandVideoIcon />}
                  size='small'
                  onClick={handleOpenViewVideo}
                  sx={
                    animatePlayVideo && {
                      animation: `${theme.palette.mode === "light" ? glowPulseLight : glowPulseDark} 2s infinite ease-in-out`,
                      boxShadow: `0 0 0 0 ${theme.palette.mode === "light" ? "rgba(101, 0, 221, 0.7)" : "rgba(171, 120, 255, 0.7)"}`,
                    }
                  }
                >
                  {t("play_video")}
                </Button>
              </Box>
            )}
            {(isMember || isCreator) && !isStudent && (
              <Box sx={{
                mr: 1
              }}>
                <DefaultButton
                  startIcon={<TimelineIcon />}
                  size='small'
                  onClick={handleOpenSelectClassroomDialog}
                >
                  {t("view_activity")}
                </DefaultButton>
              </Box>
            )}
            {classroomId && isMember && isStudent && (
              <Box sx={{
                mr: 1
              }}>
                <DefaultButton
                  startIcon={<TimelineIcon />}
                  size='small'
                  component={RouterLink}
                  to={`/classrooms/${classroomId}?courseId=${courseId}&sectionId=${sectionId}&topicId=${topicId}#Progress`}
                >
                  {t("view_activity")}
                </DefaultButton>
              </Box>
            )}
            {!isMember && !isCreator && (
              <Box sx={{
                mr: 1
              }}>
                <Button
                  variant='contained'
                  color='primary'
                  startIcon={<PersonAddIcon />}
                  size='small'
                  onClick={handleStartCourse}
                >
                  {t("join")}
                </Button>
              </Box>
            )}
            {pdfUrl && (isMember || isCreator) && (
              <Box sx={{
                mr: 1
              }}>
                <DefaultButton
                  startIcon={<PictureAsPdfIcon />}
                  size='small'
                  onClick={handleOpenViewPDF}
                >
                  {t("view_pdf")}
                </DefaultButton>
              </Box>
            )}
            {authenticated && (
              <DefaultButton
                startIcon={<MoreVertIcon />}
                size='small'
                onClick={handleClickOpenMenu}
                data-cy='more-options-button'
                aria-expanded={Boolean(anchorEl)}
              >
                {t("more_options")}
              </DefaultButton>
            )}
          </Box>
        </div>
      </div>
      {isMember && !isCreator && (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            mt: 1,
            mr: 2,
            mb: 2,
            ml: 7
          }}>
          <Box
            sx={{
              display: 'inline',
              flexGrow: 1
            }}>
            <Tooltip title={`Completed ${completed} / ${total} exercises`}>
              <ColorLinearProgress variant='determinate' value={percentage} />
            </Tooltip>
          </Box>
          <Box
            sx={{
              ml: 1,
              fontSize: 14
            }}>
            {percentage ? percentage.toFixed(0) : 0}%
          </Box>
        </Box>
      )}
      {isCreator ? (
        <ResponsiveTabs t={t} tabs={tabs} centered initialValue={initialTabValue}>
          {exercisesBox}
          <Box sx={{
            py: 1
          }}>
            <UpdateTopic
              t={t}
              initialValues={topic}
              onSubmit={handleUpdateTopic}
              fileSizeLimit={fileSizeLimit}
              subscription={subscription}
              verificationStatus={verificationStatus}
              openSnackbar={openSnackbar}
            />
          </Box>
        </ResponsiveTabs>
      ) : (
        exercisesBox
      )}
      <Grid container sx={{ width: "100%" }} spacing={1}>
        {prevTopic && prevTopic.topicId ? (
          <Grid size={{ xs: 12, md: 4 }}>
            <NavCard
              classes={classes}
              title={prevTopic.topicName}
              subtitle={t("previous")}
              picture={prevTopic.picture}
              onClick={viewPrevTopic}
              t={t}
            />
          </Grid>
        ) : (
          <Grid size={{ xs: 12, md: 4 }}>
            <NavCard
              classes={classes}
              title={t("back_to_course")}
              subtitle={t("previous")}
              onClick={viewPrevTopic}
              type={"back_to_course"}
            />
          </Grid>
        )}
        {fullScreen ? null : <Grid size={{ xs: 12, md: 4 }} />}
        {nextTopic && nextTopic.topicId ? (
          <Grid size={{ xs: 12, md: 4 }}>
            <NavCard
              classes={classes}
              title={nextTopic.topicName}
              picture={nextTopic.picture}
              subtitle={t("next")}
              onClick={viewNextTopic}
              t={t}
            />
          </Grid>
        ) : (
          <Grid size={{ xs: 12, md: 4 }}>
            <NavCard
              classes={classes}
              title={t("all_topics_completed")}
              subtitle={t("next")}
              onClick={viewNextTopic}
              type={"completed"}
            />
          </Grid>
        )}
      </Grid>
      <OverflowMenu
        t={t}
        topicId={topicId}
        anchorEl={anchorEl}
        handleClose={handleCloseMenu}
        isCreator={isCreator}
        isMember={isMember}
        joinCourse={handleStartCourse}
        leaveCourse={handleLeaveCourse}
        deleteTopic={deleteTopic}
        duplicateTopic={duplicateTopic}
        subscription={subscription}
        topicName={topicName}
        trackRecommendedEvent={trackRecommendedEvent}
        mainSiteUrl={mainSiteUrl}
        pdfUrl={pdfUrl}
        deletePDF={deletePDF}
        verificationStatus={verificationStatus}
        handleClickOpenUpload={handleClickOpenUpload}
        loading={loading}
        videoUrl={videoUrl}
        handleUpdateTopic={handleUpdateTopic}
        handleUploadVideo={handleUploadVideo}
        deleteTopicVideo={deleteTopicVideo}
        isTeacher={!isStudent}
        video={video}
        setVideo={setVideo}
        createDiscussion={createDiscussion}
      />
      <ResponsiveDialog
        open={isUploadPdfOpen}
        dialogTitle={t("upload_pdf")}
        handleClose={() => setIsUploadPdfOpen(false)}
        handleSubmit={handleUploadSubmit}
        isFullScreen={false}
        isSubmitDisabled={!file || isSubmitting}
        maxWidth={"md"}
      >
        <PdfUploader t={t} file={file} setFile={setFile} multiple={false} />
      </ResponsiveDialog>
      <VideoViewer
        t={t}
        title={topicName}
        s3Url={videoUrl}
        open={isViewVideoOpen}
        handleClose={() => setIsViewVideoOpen(false)}
      />
      <PDFViewer
        t={t}
        pdfUrl={pdfUrl}
        pdfTitle={pdfTitle}
        open={isViewPdfOpen}
        handleClose={handleCloseViewPdf}
        pageNumber={pageNumber}
        setPageNumber={setPageNumber}
        scale={scale}
        setScale={setScale}
      />
      <CreatorNotSubscribed
        t={t}
        open={isCreatorNotSubscribedOpen}
        handleClose={handleCloseCreatorNotSubscribedModal}
      />
    </>
  );
}

export default function ViewTopic({
  t = (text) => text,
  isLoading,
  topic = {},
  bots,
  roleplays,
  avatars,
  isStudent,
  handleViewCourse,
  courseSettings,
  loading,
  videoUrl,
  handleUpdateTopic,
  handleUploadVideo,
  handleDeleteTopicVideo,
  verificationStatus,
  trackRecommendedEvent,
  makeChatGptApiRequest,
  parentClassroom,
  topicGoal,
  isUserInternal,
  isReadWriteModeStudent,
  isChallengeModeStudent,
  isClassroomCreator,
  ...otherProps
}) {
  const { classes } = useStyles();
  const safeTopic = topic || {};
  return (
    <div className={classes.root}>
      {isLoading && Object.keys(safeTopic).length === 0 && (
        <Topic
          t={t}
          isLoading={isLoading}
          courseSettings={courseSettings}
          {...otherProps}
        />
      )}
      {!isLoading && topic && Object.keys(safeTopic).length && (
        <Topic
          t={t}
          isLoading={isLoading}
          topic={topic}
          bots={bots}
          roleplays={roleplays}
          avatars={avatars}
          isStudent={isStudent}
          loading={loading}
          handleUpdateTopic={handleUpdateTopic}
          handleUploadVideo={handleUploadVideo}
          handleDeleteTopicVideo={handleDeleteTopicVideo}
          courseSettings={courseSettings}
          videoUrl={videoUrl}
          verificationStatus={verificationStatus}
          trackRecommendedEvent={trackRecommendedEvent}
          makeChatGptApiRequest={makeChatGptApiRequest}
          parentClassroom={parentClassroom}
          topicGoal={topicGoal}
          isUserInternal={isUserInternal}
          isReadWriteModeStudent={isReadWriteModeStudent}
          isChallengeModeStudent={isChallengeModeStudent}
          isClassroomCreator={isClassroomCreator}
          {...otherProps}
        />
      )}
      {!isLoading && Object.keys(safeTopic).length === 0 && (
        <TopicNotFound
          t={t}
          isLoading={isLoading}
          handleViewCourse={handleViewCourse}
        />
      )}
    </div>
  );
}
